/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unstable-nested-components */
import { forwardRef } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Divider,
} from "@mui/material";
import {
  ExpandMore,
  Facebook,
  Settings,
  PhoneAndroid,
  AppRegistration,
  WarningAmber,
  InfoOutlined,
  ErrorOutline,
} from "@mui/icons-material";
import ModalLayout from "src/components/shared/atoms/modalLayout";
import YouTubePreview from "src/components/shared/molecules/channelYoutubePreviewer";

const WhatsAppSignupModal = forwardRef<
  any,
  { setModalState?: (open: boolean) => void }
>(({ setModalState }, ref) => {
  const StepItem = ({
    icon,
    title,
    description,
    caution,
  }: {
    icon: any;
    title: string;
    description: string;
    caution?: string;
  }) => (
    <Box sx={{ display: "flex", mb: 3, gap: 2 }}>
      <Avatar
        sx={{
          bgcolor: "#f0f7ff",
          color: "#4d1277",
          width: 40,
          height: 40,
          boxShadow: "0 2px 8px rgba(77, 18, 119, 0.15)",
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "#555", lineHeight: 1.6 }}>
          {description}
        </Typography>
        {caution && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 1,
              color: "#4d1277",
              fontStyle: "italic",
              fontWeight: 500,
            }}
          >
            {caution}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <ModalLayout
      headerText="WhatsApp Business Account Connection Flow"
      ref={ref}
      setModalState={setModalState}
      modalName={null}
      modalCustomStyle={{
        maxWidth: "600px",
        minHeight: "400px",
        width: "100%",
        borderRadius: "16px",
      }}
    >
      <Box
        sx={{
          maxHeight: "75vh",
          overflowY: "auto",
          p: 1,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#e0e0e0",
            borderRadius: "10px",
          },
        }}
      >
        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            mb: 2,
            color: "#4d1277",
            fontFamily: "Poppins",
            textAlign: "center",
          }}
        >
          Setup your WhatsApp Business API
        </Typography>

        {/* CRITICAL PREREQUISITE ALERT */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #fff5f5 0%, #fff 100%)",
            border: "1px solid #ffcccc",
            borderRadius: "12px",
            p: 2.5,
            mb: 4,
            boxShadow: "0 4px 12px rgba(211, 47, 47, 0.08)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "4px",
              backgroundColor: "#d32f2f",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
            <WarningAmber sx={{ color: "#d32f2f", fontSize: "1.5rem" }} />
            <Typography
              variant="subtitle2"
              sx={{ color: "#d32f2f", fontWeight: 800, fontSize: "0.95rem" }}
            >
              CRITICAL PREREQUISITE
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#2d3436", mb: 1 }}
          >
            Delete the phone number from the WhatsApp mobile app on your phone
            BEFORE starting.
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#636e72", display: "block" }}
          >
            Meta prohibits using the same number on both the mobile app and the
            Cloud API simultaneously. Not doing this will cause the connection
            to fail.
          </Typography>
        </Box>

        <YouTubePreview videoId="ELf2MlpUUNY" />

        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 800, mb: 3, color: "#2d3436" }}
          >
            Connection Steps
          </Typography>

          <StepItem
            icon={<Facebook fontSize="small" />}
            title="1. Log in with Facebook"
            description="Sign in with the Facebook account that manages your Business Suite Portfolio."
          />

          <StepItem
            icon={<Settings fontSize="small" />}
            title="2. Select or Create WABA"
            description="Choose an existing WhatsApp Business Account (WABA) or create a new one. Provide essential details like category and website."
          />

          <StepItem
            icon={<PhoneAndroid fontSize="small" />}
            title="3. Assign & Verify Number"
            description="Enter the phone number you want to use. You will receive a verification code via SMS."
            caution="Remember: The number must be unused by any other WhatsApp account."
          />

          <StepItem
            icon={<AppRegistration fontSize="small" />}
            title="4. Sync with KuikWit"
            description="Once verified on Meta, the number will be ready to add to your KuikWit CRM."
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Accordions for secondary info */}
        <Box sx={{ mb: 2 }}>
          <Accordion
            elevation={0}
            sx={{
              "&:before": { display: "none" },
              border: "1px solid #f0f0f0",
              borderRadius: "8px !important",
              mb: 1.5,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: "#fafafa" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <InfoOutlined fontSize="small" sx={{ color: "#4d1277" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Cautionary Notes
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: "#666", mb: 1.5 }}>
                � <strong>Portfolio Mapping:</strong> Your WABA is tied to a
                Meta Business Portfolio. Ensure you have one ready.
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 1.5 }}>
                � <strong>Automatic WABA Creation:</strong> Meta might
                automatically create a WABA if errors occur during selection.
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                � <strong>Wait for Verification:</strong> Meta verification can
                sometimes take a few minutes. If you see an error, wait and
                refresh the signup flow.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion
            elevation={0}
            sx={{
              "&:before": { display: "none" },
              border: "1px solid #f0f0f0",
              borderRadius: "8px !important",
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: "#fafafa" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ErrorOutline fontSize="small" sx={{ color: "#d32f2f" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Common Error Solutions
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "#2d3436" }}
                >
                  "Tried too many times"
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Wait for 1 hour. This happens if you request too many codes
                  too quickly.
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "#2d3436" }}
                >
                  "Servers temporarily unavailable"
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Wait for 5 minutes. This is a Meta-side transient issue.
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "#2d3436" }}
                >
                  "Incorrect code"
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Double-check you are using the latest code sent by Meta.
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ mt: 2, fontSize: "0.8rem" }}>
                For more, visit{" "}
                <a
                  href="https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/errors"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#4d1277" }}
                >
                  Meta Dev Docs
                </a>
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Video Guide Link */}
        <Box mt={4} mb={2} textAlign="center">
          <Typography
            variant="body2"
            mt={2}
            sx={{ fontWeight: 600, color: "#4d1277" }}
          >
            <a
              href="https://kuikwit.com/whatsapp-integration"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "inherit",
                textDecoration: "underline",
              }}
            >
              View complete WhatsApp integration guide ?
            </a>
          </Typography>
        </Box>
      </Box>
    </ModalLayout>
  );
});

export default WhatsAppSignupModal;
